import { FC, useEffect, useMemo, useState } from "react";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { Paragraph } from "@hostingbv/lib/src/components/Paragraph";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { DataTable } from "@ventumsoftware/react-toolkit";
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { config } from "../config";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { Domain } from "@hostingbv/lib/src/api/partner/models/Domain";
import { useRefresh, useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { Card } from "@hostingbv/lib/src/components/Card";
import { ObjectAutocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { SectionDivider } from "@hostingbv/lib/src/components/content/SectionDivider";
import { usePersistentState } from "@hostingbv/lib/src/util/usePersistentState";
import { Toggle } from "@hostingbv/lib/src/components/form/Toggle";
import { usePermissions } from "@hostingbv/lib/src/auth/permissions";
import { EntityNotesButton } from "../components/EntityNotesButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useModal } from "../../../lib/src/components/layout/ModalProvider";
import { ShowDnssecKeysModal } from "../modals/ShowDnssecKeysModal";
import { Copyable } from "../../../lib/src/components/content/Copyable";
import {
  faBookAtlas,
  faCancel,
  faCheckCircle,
  faCompass,
  faDownload,
  faEdit,
  faExchange,
  faGlobe,
  faKey,
  faLock,
  faPencil,
  faSave,
  faTimesCircle,
  faUnlock,
  faUpload,
  faWarning,
} from "@fortawesome/free-solid-svg-icons";
import { PageIconHeader } from "../../../lib/src/components/PageIconHeader";
import { EditUseExternalNameserversModal } from "../modals/EditUseExternalNameserversModal";
import { EntityContractControls } from "../components/EntityContractControls";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { Contact } from "@hostingbv/lib/src/api/common/models/Contact";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";

export const DomainDetails: FC = () => {
  const { uuid } = useParams();
  const { partnerClient } = useApiClient(config);
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!;
  const { resource, reload } = useFetchedResource(() => partnerClient.getDomain(uuid ?? ""));
  const domain: Domain | null = useMemo(() => (resource?.success === true ? resource.data : null), [resource]);

  useRefreshEffect(() => {
    reload();
  });
  const { can } = usePermissions(partnerClient);

  async function reactivateContract() {
    const contract = domain?.contracts.find((c) => c.cancelledAt !== null);
    if (!contract) {
      return;
    }
    const deployment = await partnerClient.reactivateContract(contract.id);
    if (deployment.success) {
      await awaitNextDeploymentFinished(deployment.data.id);
      reload();
    }
  }
  return (
    <ContentContainer size={"lg"}>
      <PageIconHeader icon={faGlobe}>
        <Breadcrumbs currentPage={domain?.displayName ?? "Domain"} crumbs={[{ label: "Domains", href: "/domains" }]} />
        <PageHeader>
          {domain === null ? (
            <span className={"block h-8 w-36 rounded-lg bg-slate-200 animate-pulse"}></span>
          ) : (
            domain.displayName
          )}
          <EntityNotesButton entity={domain} />
        </PageHeader>
      </PageIconHeader>

      <Loading loading={domain === null}>
        {domain &&
          can("edit", "contracts") &&
          domain.status === "ACT" &&
          domain.contracts.some((c) => c.cancelledAt !== null) && (
            <>
              <Callout color={"warning"} icon={faWarning}>
                <div className={"flex items-center justify-between"}>
                  <div>
                    <strong>Warning:</strong> This domain has a contract that has been cancelled.
                  </div>
                  <Button type={"primary"} size={"sm"} text={"Undo cancellation"} onClick={reactivateContract} />
                </div>
              </Callout>
            </>
          )}

        {can("view", "dns", domain?.permissions) && (
          <>
            <SectionHeader>DNS Zone</SectionHeader>
            {domain && <DNSOverview domain={domain} />}
            <SectionDivider />
          </>
        )}

        <div className={"grid grid-cols-1 xl:grid-cols-2 gap-6"}>
          {can("edit", "domains", domain?.permissions) ? (
            <div>
              {domain && (
                <>
                  <WhoisOverview domain={domain} />
                  {domain && can("edit", "contracts") && domain.contracts.length > 0 && domain.status === "ACT" && (
                    <>
                      <EntityContractControls entity={domain} />
                    </>
                  )}
                </>
              )}
            </div>
          ) : (
            can("view", "domains", domain?.permissions) &&
            domain && (
              <div>
                <ReadonlyWhoisOverview domain={domain} />
              </div>
            )
          )}
          <div>
            <StatusAndTransfer domain={domain} />
            <ImportExport domain={domain} />
          </div>
        </div>

        {domain && can("view", "contracts") && domain.contracts.length > 0 && (
          <>
            <SectionDivider />

            <SectionHeader>Contracts</SectionHeader>
            <ContractsOverview domain={domain} />
          </>
        )}
      </Loading>
    </ContentContainer>
  );
};

const WhoisOverview: FC<{ domain: Domain }> = ({ domain }) => {
  const { partnerClient } = useApiClient(config);
  const [isEditing, setIsEditing] = useState(false);

  const whoisOptions = useWhoisOptions(domain);
  const reseller = useContactOptions(
    "reseller",
    whoisOptions.customerContacts,
    whoisOptions.ownCustomerContacts,
    whoisOptions.useCustomersWhoisContacts,
    domain,
  );
  const owner = useContactOptions(
    "owner",
    whoisOptions.customerContacts,
    whoisOptions.ownCustomerContacts,
    whoisOptions.useCustomersWhoisContacts,
    domain,
  );
  const admin = useContactOptions(
    "admin",
    whoisOptions.customerContacts,
    whoisOptions.ownCustomerContacts,
    whoisOptions.useCustomersWhoisContacts,
    domain,
  );
  const tech = useContactOptions(
    "tech",
    whoisOptions.customerContacts,
    whoisOptions.ownCustomerContacts,
    whoisOptions.useCustomersWhoisContacts,
    domain,
  );

  const cancelEdit = () => {
    if (domain.customer) {
      whoisOptions.selectCustomer(domain.customer);
    }
    reseller.resetSelected();
    owner.resetSelected();
    admin.resetSelected();
    tech.resetSelected();
    setIsEditing(false);
  };
  const saveEdit = async () => {
    if (!owner.selectedOption || !admin.selectedOption || !tech.selectedOption || !reseller.selectedOption) {
      return;
    }
    await partnerClient.updateWhoisContacts(
      domain.id,
      owner.selectedOption.id,
      admin.selectedOption.id,
      tech.selectedOption.id,
      reseller.selectedOption.id,
    );
    setIsEditing(false);
  };

  return (
    <Card title={"Customer and WHOIS contact"}>
      {isEditing ? (
        <>
          <Paragraph>
            The reseller contact is a contact on your own customer. You can modify this contact in the customer details.
          </Paragraph>
          <ObjectAutocomplete<Contact>
            label={"Reseller Contact"}
            options={reseller.options}
            value={reseller.selectedOption}
            onChange={reseller.setSelectedOption}
            idProperty={"id"}
            displayItem={(c) => `${c.firstName} ${c.lastName}`}
          />
          <hr className={"my-4 -mx-6 border-slate-100"} />

          <Paragraph>
            The customer you assign may also get the rights to manage the domain if you give them access.
          </Paragraph>
          <ObjectAutocomplete<Customer>
            label={"Customer"}
            options={whoisOptions.customers}
            value={whoisOptions.customer}
            onChange={whoisOptions.selectCustomer}
            displayItem={(c) => c.displayName}
            idProperty={"id"}
          />
          <hr className={"my-4 -mx-6 border-slate-100"} />
          {owner.options.length === 0 || tech.options.length === 0 || admin.options.length === 0 ? (
            <>
              <Callout color={"warning"} icon={faWarning}>
                <strong>Warning:</strong> This customer has no contacts. You will need to add at least one contact
                before you can continue.
              </Callout>
            </>
          ) : (
            <>
              <div>
                <Toggle
                  value={whoisOptions.useCustomersWhoisContacts}
                  onChange={whoisOptions.setUseCustomersDomainContacts}
                  label={"Use the selected customer's WHOIS contacts"}
                />
              </div>
              <div className={"mt-4 flex space-x-4"}>
                <div className={"flex-1"}>
                  <ObjectAutocomplete<Contact>
                    idProperty={"id"}
                    displayItem={(c) => `${c.firstName} ${c.lastName}`}
                    label={"Owner"}
                    options={owner.options}
                    value={owner.selectedOption}
                    onChange={owner.setSelectedOption}
                  />
                </div>
                <div className={"flex-1"}>
                  <ObjectAutocomplete<Contact>
                    idProperty={"id"}
                    displayItem={(c) => `${c.firstName} ${c.lastName}`}
                    label={"Technical Contact"}
                    options={tech.options}
                    value={tech.selectedOption}
                    onChange={tech.setSelectedOption}
                  />
                </div>
                <div className={"flex-1"}>
                  <ObjectAutocomplete<Contact>
                    idProperty={"id"}
                    displayItem={(c) => `${c.firstName} ${c.lastName}`}
                    label={"Financial Contact"}
                    options={admin.options}
                    value={admin.selectedOption}
                    onChange={admin.setSelectedOption}
                  />
                </div>
              </div>
            </>
          )}
          <hr className={"my-4 -mx-6 border-slate-100"} />
          <div className={"flex space-x-2"}>
            <Button type={"primary"} size={"sm"} icon={faSave} onClick={() => saveEdit()} text={"Save changes"} />
            <Button type={"danger"} size={"sm"} icon={faCancel} onClick={() => cancelEdit()} text={"Cancel"} />
          </div>
        </>
      ) : (
        <>
          <dl className={"flex items-center h-8"}>
            <dt className={"text-slate-600 w-48"}>Reseller contact</dt>
            <dd>
              {reseller.selectedOption ? (
                `${reseller.selectedOption.firstName} ${reseller.selectedOption.lastName}`
              ) : (
                <div className={"h-4 w-20 rounded-lg bg-slate-100 animate-pulse"}></div>
              )}
            </dd>
          </dl>
          <hr className={"my-4 -mx-6 border-slate-100"} />
          <dl className={"flex items-center h-8"}>
            <dt className={"text-slate-600 w-48"}>Customer</dt>
            <dd>
              {whoisOptions.customer?.displayName ? (
                <NavLink className={"underline text-brand-600"} to={`/customers/${whoisOptions.customer.id}`}>
                  {whoisOptions.customer.displayName}
                </NavLink>
              ) : (
                <div className={"h-4 w-20 rounded-lg bg-slate-100 animate-pulse"}></div>
              )}
            </dd>
          </dl>
          <hr className={"my-4 -mx-6 border-slate-100"} />
          <dl className={"flex items-center h-8"}>
            <dt className={"text-slate-600 w-48"}>Primary Contact</dt>
            <dd>
              {owner.selectedOption ? (
                `${owner.selectedOption.firstName} ${owner.selectedOption.lastName}`
              ) : (
                <div className={"h-4 w-20 rounded-lg bg-slate-100 animate-pulse"}></div>
              )}
            </dd>
          </dl>
          <dl className={"flex items-center h-8"}>
            <dt className={"text-slate-600 w-48"}>Financial Contact</dt>
            <dd>
              {admin.selectedOption ? (
                `${admin.selectedOption.firstName} ${admin.selectedOption.lastName}`
              ) : (
                <div className={"h-4 w-20 rounded-lg bg-slate-100 animate-pulse"}></div>
              )}
            </dd>
          </dl>
          <dl className={"flex items-center h-8"}>
            <dt className={"text-slate-600 w-48"}>Technical Contact</dt>
            <dd>
              {tech.selectedOption ? (
                `${tech.selectedOption.firstName} ${tech.selectedOption.lastName}`
              ) : (
                <div className={"h-4 w-20 rounded-lg bg-slate-100 animate-pulse"}></div>
              )}
            </dd>
          </dl>
          <hr className={"my-4 -mx-6 border-slate-100"} />
          <Button type={"primary"} size={"sm"} icon={faPencil} onClick={() => setIsEditing(true)} text={"Edit"} />
        </>
      )}
    </Card>
  );
};

function ReadonlyWhoisOverview(props: { domain: Domain }) {
  const whoisOptions = useWhoisOptions(props.domain);
  const contacts = useMemo(() => {
    return {
      primary: whoisOptions.customerContacts.find((c) => c.id === props.domain.primaryContactId) ?? null,
      admin: whoisOptions.customerContacts.find((c) => c.id === props.domain.administrativeContactId) ?? null,
      tech: whoisOptions.customerContacts.find((c) => c.id === props.domain.technicalContactId) ?? null,
    };
  }, [whoisOptions.customerContacts, props.domain]);
  return (
    <Card title={"WHOIS contacts"}>
      {whoisOptions.useCustomersWhoisContacts && (contacts.primary || contacts.admin || contacts.tech) ? (
        <div className={"flex flex-wrap gap-4"}>
          {contacts.primary && <ReadonlyWhoisOverviewContact title={"Owner"} contact={contacts.primary} />}
          {contacts.admin && <ReadonlyWhoisOverviewContact title={"Administrative contact"} contact={contacts.admin} />}
          {contacts.tech && <ReadonlyWhoisOverviewContact title={"Technical contact"} contact={contacts.tech} />}
        </div>
      ) : (
        <div className={"text-center text-slate-500"}>
          This domain is not configured to use the WHOIS contacts in your account.
        </div>
      )}
    </Card>
  );
}
function ReadonlyWhoisOverviewContact(props: { title: string; contact: Contact }) {
  return (
    <div className={"border border-slate-100 rounded-sm w-64 space-y-2 p-4"}>
      <h2 className={"text-xs text-slate-700 uppercase"}>{props.title}</h2>
      <div className={"text-base font-medium"}>
        {props.contact.firstName} {props.contact.lastName}
      </div>
      <div className={"text-sm text-slate-700"}>{props.contact.email}</div>
    </div>
  );
}

function DNSOverview(props: { domain: Domain }) {
  const navigate = useNavigate();
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const { open: openExternalNameserversModal } = useModal({
    title: "External Nameservers",
    size: "md",
    body: <EditUseExternalNameserversModal domain={props.domain} external={true} />,
  });
  const { open: openInternalNameserversModal } = useModal({
    title: "Internal Nameservers",
    size: "md",
    body: <EditUseExternalNameserversModal domain={props.domain} external={false} />,
  });
  const { getRealtimeStatus } = useRealtimeDeployments()!;
  const status = getRealtimeStatus(props.domain.id, props.domain.deploymentStatus);
  return (
    <div className={"mb-8"}>
      <Callout>
        {props.domain.externalDns ? (
          <>
            <SectionHeader>External Nameservers</SectionHeader>
            <Paragraph>Currently, this domain is configured to use external nameservers.</Paragraph>
            <ul className={"bg-slate-200 rounded-sm max-w-md my-4 py-1"}>
              {props.domain.nameservers.map((ns, index) => (
                <li className={"flex items-center my-1 px-3"} key={index}>
                  <span className={"w-12 text-sm font-medium text-brand-900"}>
                    NS{index < 9 ? "0" : ""}
                    {index + 1}
                  </span>
                  <div className={"text-xs font-mono text-slate-800"}>{ns}</div>
                </li>
              ))}
            </ul>
            {can("edit", "dns", props.domain.permissions) && (
              <div className={"flex items-center space-x-4"}>
                <Button
                  type={"primary"}
                  size={"sm"}
                  icon={faBookAtlas}
                  text={"Use our nameservers"}
                  onClick={() => openInternalNameserversModal()}
                  disabled={status === "pending" || status === "running"}
                />
                <Button
                  type={"secondary"}
                  size={"sm"}
                  icon={faEdit}
                  text={"Edit nameservers"}
                  onClick={() => openExternalNameserversModal()}
                  disabled={status === "pending" || status === "running"}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <div className={"flex"}>
              <div className={"flex-1"}>
                <SectionHeader>DNS Powered by Cloudflare</SectionHeader>
                <Paragraph>
                  In order to provide best-in-class DNS services, we have partnered with Cloudflare to provide DNS
                  services for your domain.
                </Paragraph>
                <div className={"flex items-center space-x-4"}>
                  <Button
                    type={"primary"}
                    size={"sm"}
                    icon={faBookAtlas}
                    text={can("edit", "dns", props.domain.permissions) ? "Manage DNS" : "View DNS"}
                    onClick={() => navigate(`/domains/${props.domain.id}/dns`)}
                    disabled={status === "pending" || status === "running"}
                  />
                  {can("edit", "dns", props.domain.permissions) && (
                    <Button
                      type={"secondary"}
                      size={"sm"}
                      icon={faCompass}
                      text={"Use external nameservers"}
                      onClick={() => openExternalNameserversModal()}
                      disabled={status === "pending" || status === "running"}
                    />
                  )}
                </div>
              </div>
              <div>
                <div className={"bg-white rounded-md p-4 w-48 flex flex-col items-start justify-center"}>
                  <span className={"text-slate-400 font-medium text-sm"}>Powered by</span>
                  <img src="/cloudflare.png" alt="Cloudflare" className={"w-full"} />
                </div>
              </div>
            </div>
          </>
        )}
      </Callout>
    </div>
  );
}

function ContractsOverview(props: { domain: Domain }) {
  return (
    <div className={"mb-8"}>
      <DataTable
        keyProperty={"id"}
        data={props.domain.contracts}
        columns={[
          { property: "displayName", header: "Description" },
          {
            property: "renewalAt",
            header: "Next invoice",
            transform: (v, c) => {
              if (c.cancelledAt !== null) {
                return <span className={"bg-red-600 text-white font-medium text-xs px-2 rounded-full"}>Cancelled</span>;
              }
              return v.toLocaleDateString("nl");
            },
          },
          {
            property: "billingPeriod",
            header: "Invoice period",
            transform: (v) => `Every ${v} ${v > 1 ? "months" : "month"}`,
          },
          { property: "amount", header: "Amount (Excl. VAT)", transform: (v) => `€ ${(v / 100).toFixed(2)}` },
        ]}
      />
    </div>
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- shared hook colocated with the page that owns it
export function useWhoisOptions(domain?: Domain) {
  const [useDefaultCustomersWhoisContacts, setDefaultUseCustomersDomainContacts] = usePersistentState<boolean>(
    "use_customers_whois_contacts",
    false,
  );
  const [useCustomerWhoisContacts, setUseCustomerWhoisContacts] = useState<boolean | null>(null);

  const { partnerClient } = useApiClient(config);
  const [customerOptions, setCustomerOptions] = useState<Customer[]>([]);
  useEffect(() => {
    partnerClient
      .getAllCustomers()
      .then((response) => (response.success ? response.data : []))
      .then((customers) => setCustomerOptions(customers));
  }, []);

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(domain?.customer ?? null);
  const selectedCustomerContacts = useCustomerContacts(selectedCustomer);
  // When there is no option where the customer is the owner, use the first option. In this case, the user is probably not an employee
  const ownCustomer = useMemo(
    () => customerOptions.find((c) => c.isTenantOwner) ?? customerOptions[0],
    [customerOptions],
  );
  const ownCustomerContacts = useCustomerContacts(ownCustomer);
  useEffect(() => {
    if (useCustomerWhoisContacts === null && domain) {
      const usesOwnContacts = ownCustomerContacts.map((c) => c.id).includes(domain.technicalContactId);
      setUseCustomerWhoisContacts(!usesOwnContacts);
    }
  }, [ownCustomerContacts, domain, useCustomerWhoisContacts]);

  useEffect(() => {
    if (customerOptions.length > 0 && selectedCustomer === null) {
      setSelectedCustomer(customerOptions[0]);
    }
  }, [selectedCustomer, customerOptions]);

  return {
    customer: selectedCustomer ?? customerOptions[0] ?? null,
    customers: customerOptions,
    customerContacts: selectedCustomerContacts,
    ownCustomerContacts: ownCustomerContacts,
    selectCustomer: setSelectedCustomer,
    // Option for which contacts to use
    useCustomersWhoisContacts: domain
      ? (useCustomerWhoisContacts ?? useDefaultCustomersWhoisContacts)
      : useDefaultCustomersWhoisContacts,
    setUseCustomersDomainContacts: domain
      ? (setUseCustomerWhoisContacts ?? setDefaultUseCustomersDomainContacts)
      : setDefaultUseCustomersDomainContacts,
  };
}

// eslint-disable-next-line react-refresh/only-export-components -- shared hook colocated with the page that owns it
export function useContactOptions(
  type: "owner" | "admin" | "tech" | "reseller",
  customerContacts: Contact[],
  ownCustomerContacts: Contact[],
  useCustomersWhoisContacts: boolean,
  domain?: Domain,
) {
  const [selectedOption, setSelectedOption] = useState<Contact | null>(null);
  // Provide the currently selected value (if available) as default value for selected.
  const oldContact = useMemo(() => {
    let contactUuid: string | null = null;
    switch (type) {
      case "owner":
        contactUuid = domain?.primaryContactId ?? null;
        break;
      case "admin":
        contactUuid = domain?.administrativeContactId ?? null;
        break;
      case "tech":
        contactUuid = domain?.technicalContactId ?? null;
        break;
      case "reseller":
        contactUuid = domain?.resellerContactId ?? null;
        break;
    }
    return contactUuid
      ? (customerContacts.find((c) => c.id === contactUuid) ??
        ownCustomerContacts.find((c) => c.id === contactUuid) ??
        null)
      : null;
  }, [domain, type, customerContacts, ownCustomerContacts]);

  const options = useMemo(() => {
    if (type === "reseller") {
      return ownCustomerContacts.filter((c) => c.type !== "internal");
    }
    if (type === "owner") {
      return customerContacts.filter((c) => c.type === "primary");
    }
    const pool = useCustomersWhoisContacts ? customerContacts : ownCustomerContacts;
    return pool.filter((c) => c.type !== "internal");
  }, [useCustomersWhoisContacts, customerContacts, ownCustomerContacts]);

  // The default value is the suggested option based on the type.
  const defaultContact = useMemo(() => {
    switch (type) {
      case "owner":
        return options.find((c) => c.type === "primary") ?? null;
      case "admin":
        return options.find((c) => c.type === "financial") ?? options.find((c) => c.type === "primary") ?? null;
      case "tech":
        return options.find((c) => c.type === "technical") ?? options.find((c) => c.type === "primary") ?? null;
      case "reseller":
        return options.find((c) => c.type === "primary") ?? null;
    }
    return null;
  }, [options, type]);

  const resetSelected = () => setSelectedOption(null);

  useEffect(() => {
    if (selectedOption !== null && !options.map((o) => o.id).includes(selectedOption.id)) {
      // The selected option is not in the list of options, so reset it.
      resetSelected();
    }
  }, [options, selectedOption]);

  return {
    options,
    selectedOption: selectedOption ?? oldContact ?? defaultContact,
    setSelectedOption,
    resetSelected,
  };
}

function useCustomerContacts(customer: Customer | null) {
  const { partnerClient } = useApiClient(config);
  const [selectedCustomerContacts, setSelectedCustomerContacts] = useState<Contact[]>([]);
  /**
   * When the selected customer changes, fetch their contacts and store them in the selectedCustomerContacts state.
   */
  useEffect(() => {
    if (customer) {
      partnerClient
        .getCustomer(customer.id)
        .then((response) => (response.success ? response.data.contacts : []))
        .then((contacts) => setSelectedCustomerContacts(contacts));
    }
  }, [customer]);
  return selectedCustomerContacts;
}

const StatusAndTransfer: FC<{ domain: Domain | null }> = ({ domain }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const reload = useRefresh();
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!;
  const { open: openDnssecKeysModal } = useModal({
    title: "DNSSEC Keys",
    size: "lg",
    body: <ShowDnssecKeysModal keys={domain?.dnssecKeys ?? []} />,
  });
  const [fetchedCode, setFetchedCode] = useState<string | null>(null);
  const [codeError, setCodeError] = useState("");
  const fetchCode = async () => {
    if (!domain) {
      return;
    }
    if (domain.tld === "be") {
      window.open(`https://www.dnsbelgium.be/nl/whois/info/${domain.displayName}/transfer-code`, "_blank");
      return;
    }
    setCodeError("");
    const result = await partnerClient.getDomainTransferSecret(domain.id);
    if (!result.success) {
      setCodeError(result.error);
      return;
    }
    setFetchedCode(result.data);
  };
  const toggleTransferLock = async () => {
    if (!domain || !domain.isLockable) {
      return;
    }
    const deployment = domain.isLocked
      ? await partnerClient.disableTransferLock(domain.id)
      : await partnerClient.enableTransferLock(domain.id);

    if (deployment.success) {
      await awaitNextDeploymentFinished(deployment.data.id);
      reload();
    }
  };
  return (
    <Card title={"Status and Transfer"}>
      <dl className={"flex items-center h-8"}>
        <dt className={"text-slate-600 w-48"}>Status</dt>
        <dd>{domain?.status ?? "n/a"}</dd>
      </dl>
      <dl className={"flex items-center h-8"}>
        <dt className={"text-slate-600 w-48"}>DNSSEC</dt>
        <dd>
          {domain?.isDnssecEnabled === null ? (
            "n/a"
          ) : domain?.isDnssecEnabled ? (
            <>
              <span className={"text-green-600"}>
                <FontAwesomeIcon icon={faCheckCircle} className={"text-green-600 mr-1"} /> Enabled
              </span>
            </>
          ) : (
            <>
              <span className={"text-red-600"}>
                <FontAwesomeIcon icon={faTimesCircle} className={"text-red-600 mr-1"} /> Disabled
              </span>
            </>
          )}
        </dd>
      </dl>
      <dl className={"flex items-center h-8"}>
        <dt className={"text-slate-600 w-48"}>Transfer lock</dt>
        {domain?.isLockable ? (
          <>
            <dd>
              {domain.isLocked ? (
                <>
                  <span className={"text-green-600"}>
                    <FontAwesomeIcon icon={faLock} className={"text-green-600 mr-1"} /> Locked
                  </span>
                </>
              ) : (
                <>
                  <span className={"text-amber-600"}>
                    <FontAwesomeIcon icon={faUnlock} className={"text-amber-600 mr-1"} /> Unlocked
                  </span>
                </>
              )}
            </dd>
          </>
        ) : (
          <>
            <dd>Not available</dd>
          </>
        )}
        {/*<dd>{domain?.renewalDate?.toLocaleDateString('nl') ?? 'n/a'}</dd>*/}
      </dl>
      {domain?.isLockable && can("edit", "domains", domain?.permissions) && (
        <>
          <hr className={"my-4 -mx-6 border-slate-100"} />
          <div className={"flex flex-col items-stretch"}>
            <Button
              type={"secondary"}
              size={"sm"}
              text={domain.isLocked ? "Disable transfer lock" : "Enable transfer lock"}
              icon={domain.isLocked ? faUnlock : faLock}
              onClick={() => toggleTransferLock()}
            />
          </div>
        </>
      )}
      {domain?.isDnssecEnabled && can("view", "domains", domain?.permissions) && (
        <>
          <hr className={"my-4 -mx-6 border-slate-100"} />
          <div className={"flex flex-col items-stretch"}>
            <Button
              type={"secondary"}
              size={"sm"}
              text={"Show DNSSEC keys"}
              icon={faKey}
              onClick={() => openDnssecKeysModal()}
            />
          </div>
        </>
      )}
      {can("view", "domainAuth", domain?.permissions) && (
        <>
          <hr className={"my-4 -mx-6 border-slate-100"} />
          <div className={"flex flex-col items-stretch"}>
            {fetchedCode ? (
              <>
                <dl className={"flex items-center h-8"}>
                  <dt className={"text-slate-600 w-48"}>Code</dt>
                  <dd>
                    <Copyable text={fetchedCode} />
                  </dd>
                </dl>
              </>
            ) : (
              <>
                <Button
                  type={"secondary"}
                  size={"sm"}
                  text={"Request Transfer Secret"}
                  icon={faExchange}
                  disabled={domain?.status === "DEL"}
                  onClick={fetchCode}
                />
                {codeError.length > 0 && <div className={"font-medium text-red-600 mt-2"}>{codeError}</div>}
              </>
            )}
          </div>
        </>
      )}
    </Card>
  );
};
const ImportExport: FC<{ domain: Domain | null }> = ({ domain }) => {
  const { partnerClient } = useApiClient(config);
  const { can } = usePermissions(partnerClient);
  const navigate = useNavigate();
  const exportZone = async () => {
    if (domain === null) {
      return;
    }
    const content = await partnerClient.exportDnsZone(domain.id);
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement("a");
    a.setAttribute("href", url);
    a.setAttribute("download", `${domain.displayName}.zone.txt`);
    a.click();
  };
  return (
    <Card title={"Import and Export DNS"}>
      <Paragraph>You can directly import and export entire DNS zones using a Zone file.</Paragraph>
      <div className={"grid grid-cols-1 md:grid-cols-2 gap-4"}>
        <Button
          type={"secondary"}
          size={"lg"}
          text={"Import DNS zone"}
          icon={faUpload}
          onClick={() => navigate("dns/import")}
          disabled={!can("edit", "dns", domain?.permissions)}
        />
        <Button
          type={"secondary"}
          size={"lg"}
          text={"Export DNS zone"}
          icon={faDownload}
          onClick={exportZone}
          disabled={domain?.externalDns || !can("view", "dns", domain?.permissions)}
        />
      </div>
    </Card>
  );
};
